import { useEffect, useMemo, useRef, useState } from "react";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";

import hrchyConfigService from "@plannel/services/data/hrchy-config-service"
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";

import { isEmpty } from "lodash";

const { snackBarMessageColor } = ColorUtil;
const HrchyConfig = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [hrchyRowData, setHrchyRowData] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = [
    {
      headerName: "hrchyGrpCd",
      field: "grpCd",
      minWidth: 140, flex: 1,
      cellClass: 'stringType',
      //valueSetter: setGrpCode
      filterType: "string"
    },
    { headerName: "hrchyGrpName", field: "grpName", minWidth: 140, flex: 1.5, filterType: "string" }, //valueSetter: setGrpName
    { headerName: "hrchyLvCd", field: "cd", cellClass: 'stringType', minWidth: 130, flex: 1, filterType: "string" },
    { headerName: "hrchyLvName", field: "name", minWidth: 140, flex: 1.5, filterType: "string" },
    { headerName: "hrchyNum", field: "hrchyNum", type: ["numberColumn"], minWidth: 100, flex: 0.6, filterType: "number" },
    { headerName: "seq", field: "seqNum", type: ["numberColumn"],
      valueSetter: params => {
        const newValue = Number(params.newValue);
        if (!Number.isFinite(newValue) || newValue < 1) {
          return false;
        }
        params.data.seqNum = params.newValue;
        return true;
      },
      minWidth: 100, flex: 0.6, filterType: "number" },
    { headerName: "itemHrchy", field: "itemHrchyFlg", type: ["booleanColumn"], minWidth: 100, flex: 0.6, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 1, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 1, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const retrieveHrchyConfigs = () => {
    const params = {};
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }
    hrchyConfigService.getAll(params)
      .then((response) => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setHrchyRowData(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const addRow = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    const newRow = {
      id: null,
      grpCd: "",
      grpName: "",
      cd: "",
      name: "",
      hrchyNum: 1,
      seqNum: 0,
      itemHrchyFlg: false,
      verNum: 0
    };

    if (selectedRows.length > 0) {
      const selectedRow = selectedRows[selectedRows.length - 1];
      newRow.grpCd = selectedRow.grpCd;
      newRow.grpName = selectedRow.grpName;
      newRow.seqNum = selectedRow.seqNum + 1;
      newRow.itemHrchyFlg = selectedRow.itemHrchyFlg;
    }

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const saveHrchyConfig = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      hrchyConfigService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          retrieveHrchyConfigs();
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const removeHrchyConfig = () => {
    const hrchyIds = [];
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        hrchyIds.push(data.id);
      }
    );

    if (hrchyIds.length > 0) {
      hrchyConfigService.remove(hrchyIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveHrchyConfigs();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveHrchyConfig();
    } else if (action === "DELETE") {
      removeHrchyConfig();
    }
  }

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  useEffect(() => {
    DataState.initialize(gridRef.current.api);
    retrieveHrchyConfigs();

    return () => {
      setHrchyRowData([]);
    }

    // eslint-disable-next-line
  }, [advancedFilters])

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "right" }}>
        <ExcelExportButton title={title} service={hrchyConfigService} columnDefs={columnDefs} params={{advancedFilters}}/>
        <AddButton onClick={addRow} />
        <RemoveButton onClick={() => {
          gridRef.current?.api?.stopEditing();
          setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.hrchyConfig, 'delete')}` })
        }} />
        <SaveButton onClick={() => {
          gridRef.current?.api?.stopEditing();
          setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })
        }} />
        <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
      </Box>
        <Box className="ag-theme-balham grid-area" mt={1} sx={{ height: "calc(100% - 50px)" }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={hrchyRowData}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  )
};

export default withTranslation()(HrchyConfig);